<!-- Loading / "thinking" turn (PCSA-13160). 13174 may later animate dots. -->
<div *ngIf="message.type === OrbiMessageType.Loading" class="orbi-message orbi-message--agent orbi-message--loading">
  <div class="orbi-message__bubble">
    <span class="orbi-message__loading-text">Give me one moment to get that</span>
    <span class="orbi-message__dots" aria-hidden="true"> <span></span><span></span><span></span> </span>
  </div>
</div>

<!-- User turn: right-aligned navy bubble. -->
<div *ngIf="message.type === OrbiMessageType.User" class="orbi-message orbi-message--user">
  <div class="orbi-message__bubble">{{ message.text }}</div>
</div>

<!-- Agent text turn (PCSA-13183 / PCSA-13242): left-aligned white bubble, rendered
     as markdown via ngx-markdown (PCSA-13174, sanitized in PortalOrbiModule).
     Renders text-routed turns AND guardrail-routed turns (guardrail responses
     render via the text path, showing the agent's TextualResponse verbatim —
     13242 AC 3/8). The response feedback + copy row (PCSA-13171) is shown ONLY
     for the `text` route (not guardrail), tied to the response by messageId. -->
<div *ngIf="isTextTurn" class="orbi-message orbi-message--agent">
  <div #bubble class="orbi-message__bubble" [attr.tabindex]="isGuardrailLockTurn ? -1 : null">
    <markdown class="orbi-message__markdown" [data]="message.text" (ready)="onMarkdownReady()"></markdown>
    <patient-ui-orbi-feedback
      *ngIf="(message.route === 'text' || isAppointmentScheduleTurn) && message.messageId"
      [message]="message"
    ></patient-ui-orbi-feedback>
  </div>
</div>

<!-- Results card turn (PCSA-13224): a PatientResults response renders the typed
     accessions as a structured card (behavior/data = mobile, layout = Figma).
     Its navigation intents bubble up via (navigate) for the thread to route +
     close the panel. Wrapped in the agent bubble so it aligns like other agent
     turns. Badges are OFF by default (mobile Orbi cards pass showBadge: false). -->
<div *ngIf="isResultsCardTurn" class="orbi-message orbi-message--agent orbi-message--card">
  <div class="orbi-message__bubble orbi-message__bubble--card">
    <patient-ui-orbi-results-card [accessions]="resultsAccessions" (navigate)="onCardNavigate($event)"></patient-ui-orbi-results-card>
  </div>
</div>

<!-- Appointment card turns (PCSA-13241 / PCSA-13374 / PCSA-13397). All five
     appointment return types share the `appointment-card` route, so the renderer
     fans out on returnType. Unlike the Results card these do NOT own the turn:
     the agent's text bubble renders above the card (mobile `_buildCardBubble`).
     AppointmentCancelled intentionally has no card — its confirmation text is the
     whole answer. -->

<!-- AppointmentDetails → the Upcoming Appointment card set, then a Visit History
     card for its past rows. That card's See All is `appointmentsSeeAll` on purpose. -->
<div *ngIf="hasUpcomingAppointmentsCard" class="orbi-message orbi-message--agent orbi-message--card">
  <div class="orbi-message__bubble orbi-message__bubble--card">
    <patient-ui-orbi-appointment-card
      [appointments]="upcomingAppointments"
      (navigate)="onAppointmentNavigate($event)"
      (seeAll)="onAppointmentsSeeAll()"
    ></patient-ui-orbi-appointment-card>
  </div>
</div>
<div *ngIf="hasPastAppointmentsCard" class="orbi-message orbi-message--agent orbi-message--card">
  <div class="orbi-message__bubble orbi-message__bubble--card">
    <patient-ui-orbi-visit-history-card
      [visits]="pastAppointments"
      (seeAll)="onAppointmentsSeeAll()"
      (rateVisit)="onRateVisit($event)"
    ></patient-ui-orbi-visit-history-card>
  </div>
</div>

<!-- VisitHistory → the Visit History card. Same mapped rows as above, different
     renderer: past visits nest inside one headed card with Rate Visit / See All. -->
<div *ngIf="isVisitHistoryTurn" class="orbi-message orbi-message--agent orbi-message--card">
  <div class="orbi-message__bubble orbi-message__bubble--card">
    <patient-ui-orbi-visit-history-card
      [visits]="appointments"
      (seeAll)="onVisitHistorySeeAll()"
      (rateVisit)="onRateVisit($event)"
    ></patient-ui-orbi-visit-history-card>
  </div>
</div>

<!-- AppointmentPatientPass → the Labcorp Patient Pass QR card. -->
<div *ngIf="isPatientPassTurn" class="orbi-message orbi-message--agent orbi-message--card">
  <div class="orbi-message__bubble orbi-message__bubble--card">
    <patient-ui-orbi-patient-pass-card
      [data]="patientPassData"
      (addToWallet)="addToWallet.emit($event)"
    ></patient-ui-orbi-patient-pass-card>
  </div>
</div>

<!-- AppointmentFindLab → the Make an Appointment search card. -->
<div *ngIf="isFindLabTurn" class="orbi-message orbi-message--agent orbi-message--card">
  <div class="orbi-message__bubble orbi-message__bubble--card">
    <patient-ui-orbi-find-lab-card [data]="findLabData" (search)="onFindLabSearch($event)"></patient-ui-orbi-find-lab-card>
  </div>
</div>

<!-- Billing card turn (PCSA-13419). All six billing return types share the
     `billing-card` route, so the renderer fans out on returnType; only FindABill
     has a card today (the five navigation types are PCSA-13416). Like the
     appointment cards it does NOT own the turn — the agent's text bubble renders
     above it, which is what explains WHY the form is being asked for. -->
<div *ngIf="isFindABillTurn" class="orbi-message orbi-message--agent orbi-message--card">
  <div class="orbi-message__bubble orbi-message__bubble--card">
    <patient-ui-orbi-find-a-bill-card
      [invoiceNumbers]="invoiceNumbers"
      (found)="onFindABillFound($event)"
    ></patient-ui-orbi-find-a-bill-card>
  </div>
</div>

<!-- Fallback turn (PCSA-13183 AC 5 / PCSA-13223 `fallback`): an agent turn with
     no renderable content shows the approved fallback copy instead of an empty
     bubble. -->
<div *ngIf="isFallbackTurn" class="orbi-message orbi-message--agent orbi-message--fallback">
  <div class="orbi-message__bubble">{{ fallbackText }}</div>
</div>

<!-- Recoverable error bubble (PCSA-13172): friendly, non-technical message in an
     agent-styled bubble with role="alert" (SR-announced). The error state never
     locks the experience (AC 4) — the input stays live, so the way forward is to
     ask again rather than to press anything in the bubble.

     TEXT ONLY, no control (design review 08/28, Figma "UX FEEDBACK" -> Container:
     "no button anymore, only text"). This reverses 08/18's "Replace with button",
     which had in turn replaced an underlined "Try again" link — the bubble has now
     been through link -> pill -> nothing, and the copy moved with it: it reads
     "Please try again later" rather than naming an action the bubble no longer
     offers.

     The retry PLUMBING is deliberately left in place — `retryLastMessage`, its
     effect, the reducer's `lastUserMessage` stash and `canRetry` on the message.
     It is intentionally unreachable from the UI, not dead: this decision has
     flipped twice, and restoring the control is one `ui-button` here. Nothing
     else calls it, so anything else it touches can be read as unused. -->
<div *ngIf="message.type === OrbiMessageType.Error" class="orbi-message orbi-message--agent orbi-message--error">
  <div class="orbi-message__bubble" role="alert">
    <span class="orbi-message__error-text">{{ message.text }}</span>
  </div>
</div>
